import {faFileArrowUp} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Modal} from 'antd';
import Text from 'antd/lib/typography/Text';
import {SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Crop, ReactCrop} from 'react-image-crop';

import {Locale} from '../../../../../../../provider/locale/localization';
import {useModal} from '../../../../../../../provider/modal/modal-hook';
import {CompanyImageFormType} from '../../../../../../../service/company-v2/types/company-logo-type';
import {classNames} from '../../../../../../../util/css';
import {useRefreshId} from '../../../../../../../util/hook';
import {getCropImage, getImageFromUrl, RealSizeType} from '../../../../../../../util/image';
import {catchError} from '../../../../../../../util/promise';
import {DEFAULT_INPUT_IMAGE} from '../../../../../bulk-edit-companies/form-items/company-form-items/logo-form-item-v2/input-image/images-const';

import {InputImageContent} from './input-image-content/input-image-content';
import {UploadButton} from './upload-button/upload-button';
import * as styles from './input-image.scss';

type InputImageType = CompanyImageFormType & {
    file?: File | null;
};

type PropsType = {
    className?: string;
    minWidth: number;
    minHeight: number;
    inputImage: InputImageType;
    modalHeader: JSX.Element;
    noImageText: JSX.Element;
    setInputImage: (inputImage: InputImageType) => void;
    onUpload: (image: File) => void;
    onRemove: () => void;
};

// eslint-disable-next-line max-statements
export function InputImage(props: PropsType): JSX.Element {
    const {className, minWidth, minHeight, inputImage, modalHeader, noImageText, setInputImage, onUpload, onRemove} =
        props;
    const {objectUrl, url} = inputImage;
    const targetRef = useRef<HTMLDivElement>(null);
    const drawImageUrl = url || objectUrl;
    const {refreshId, refresh} = useRefreshId();
    const {modal} = useModal();

    const [inputActive, setInputActive] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [crop, setCrop] = useState<Crop>({x: 0, y: 0, width: minWidth, height: minHeight, unit: 'px'});
    const [relativeRealSize, setRelativeRealSize] = useState<RealSizeType>({width: 1, height: 1});
    const [minLocalSize, setMinLocalSize] = useState<RealSizeType>({width: minWidth, height: minHeight});
    const [imageSize, setImageSize] = useState<RealSizeType>({width: 0, height: 0}); // Добавим состояние для размеров изображения

    const [innerInputImage, setInnerInputImage] = useState<File | null>(null);
    const originalUploadByteBudgetRef = useRef<number | null>(null);

    const isLogo = useMemo(() => minWidth === minHeight, [minWidth, minHeight]);

    const validateCropBounds = useCallback((newCrop: Crop, imageWidth: number, imageHeight: number): Crop => {
        const {width, height} = newCrop;
        let {x, y} = newCrop;

        x = x < 0 ? 0 : x;
        y = y < 0 ? 0 : y;

        const maxX = imageWidth - width;
        const maxY = imageHeight - height;

        x = x > maxX ? maxX : x;
        y = y > maxY ? maxY : y;

        return {
            ...newCrop,
            x,
            y,
        };
    }, []);

    const resetImage = useCallback(() => {
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
        }

        originalUploadByteBudgetRef.current = null;

        setInputImage(DEFAULT_INPUT_IMAGE);

        refresh();

        onRemove();
    }, [refresh, objectUrl, setInputImage, onRemove]);

    const getUploadImageError = useCallback(
        (isUploadingFile: boolean) => {
            resetImage();

            if (isUploadingFile) {
                modal.error({
                    title: <Locale stringKey="COMPANY_FORM__LOGO__ERROR__INVALID_IMAGE" />,
                    content: <Locale stringKey="COMPANY_FORM__LOGO__ERROR__INVALID_IMAGE_DESCRIPTION" />,
                });
            }

            return null;
        },
        [modal, resetImage]
    );

    const changeSizeParameters = useCallback(
        (newObjectUrl: string) => {
            if (newObjectUrl) {
                getImageFromUrl(newObjectUrl)
                    .then((image: HTMLImageElement) => {
                        const currentWidth = targetRef?.current?.children[0]?.clientWidth || minWidth;
                        const currentHeight = targetRef?.current?.children[0]?.clientHeight || minHeight;
                        const {naturalWidth, naturalHeight} = image;

                        setImageSize({width: naturalWidth, height: naturalHeight});

                        const ratioOfRealToMinWidth = naturalWidth > minWidth ? naturalWidth / minWidth : 1;
                        const ratioOfRealToMinHeight = naturalHeight > minHeight ? naturalHeight / minHeight : 1;

                        if (isLogo) {
                            setRelativeRealSize({
                                width: naturalWidth / currentWidth,
                                height: naturalWidth / currentWidth,
                            });
                        } else {
                            setRelativeRealSize({
                                width: naturalWidth / currentWidth,
                                height: naturalHeight / currentHeight,
                            });
                        }

                        setMinLocalSize({
                            width: currentWidth / ratioOfRealToMinWidth,
                            height: currentHeight / ratioOfRealToMinHeight,
                        });

                        const initialWidth = currentWidth / ratioOfRealToMinWidth;
                        const initialHeight = currentHeight / ratioOfRealToMinHeight;

                        const validatedCrop = validateCropBounds(
                            {
                                x: 0,
                                y: 0,
                                width: initialWidth,
                                height: initialHeight,
                                unit: 'px',
                            },
                            naturalWidth,
                            naturalHeight
                        );

                        setCrop(validatedCrop);
                    })
                    .catch(() => getUploadImageError(false));
            }
        },
        [getUploadImageError, isLogo, minHeight, minWidth, validateCropBounds]
    );

    const resizeWindow = useCallback(() => {
        if (objectUrl) {
            changeSizeParameters(objectUrl);
        }
    }, [objectUrl, changeSizeParameters]);

    useEffect(() => {
        window.addEventListener('resize', resizeWindow);

        return () => {
            window.removeEventListener('resize', resizeWindow);
        };
    }, [resizeWindow]);

    const handleSetCrop = useCallback(
        (newCrop: Crop) => {
            let width = newCrop.width < minLocalSize.width ? minLocalSize.width : newCrop.width;
            let height = newCrop.height < minLocalSize.height ? minLocalSize.height : newCrop.height;
            const currentWidth = targetRef?.current?.children[0]?.clientWidth || minWidth;
            const currentHeight = targetRef?.current?.children[0]?.clientHeight || minHeight;

            if (isLogo) {
                const size = newCrop.width > newCrop.height ? newCrop.width : newCrop.height;
                const minSide = currentWidth > currentHeight ? currentHeight : currentWidth;
                const minSideSize = size > minSide ? minSide : size;

                width = minSideSize;
                height = minSideSize;
            } else {
                height = Math.ceil((width * 9) / 16);
            }

            const localImageWidth = imageSize.width / relativeRealSize.width;
            const localImageHeight = imageSize.height / relativeRealSize.height;

            const validatedCrop = validateCropBounds(
                {
                    ...newCrop,
                    width,
                    height,
                },
                localImageWidth,
                localImageHeight
            );

            setCrop(validatedCrop);
        },
        [minLocalSize, minWidth, minHeight, isLogo, imageSize, relativeRealSize, validateCropBounds]
    );

    const handleCropChange = useCallback(
        (newCrop: Crop) => {
            const localImageWidth = imageSize.width / relativeRealSize.width;
            const localImageHeight = imageSize.height / relativeRealSize.height;

            const validatedCrop = validateCropBounds(newCrop, localImageWidth, localImageHeight);

            setCrop(validatedCrop);
        },
        [imageSize, relativeRealSize, validateCropBounds]
    );

    const applyChanges = useCallback(() => {
        getCropImage(drawImageUrl || '', crop, relativeRealSize, {
            ...(originalUploadByteBudgetRef.current !== null
                ? {maxOutputBytes: originalUploadByteBudgetRef.current}
                : {}),
        })
            .then((blob: Blob) => {
                if (objectUrl) {
                    URL.revokeObjectURL(objectUrl);
                }

                const filename: string = innerInputImage?.name || JSON.stringify(crop);

                const croppedFile = new File([blob], filename);

                setInputImage({
                    tempId: null,
                    objectUrl: URL.createObjectURL(croppedFile),
                    url: '',
                    file: croppedFile,
                });

                setIsModalOpen(false);

                onUpload(croppedFile);
            })
            .catch(catchError);
    }, [drawImageUrl, crop, objectUrl, innerInputImage?.name, setInputImage, onUpload, relativeRealSize]);

    const handleOnChange = useCallback(
        (event: SyntheticEvent<HTMLInputElement>) => {
            const {files} = event.currentTarget;
            const newFile: File | null = files && files[0] ? files[0] : null;

            originalUploadByteBudgetRef.current = newFile ? newFile.size : null;

            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }

            setInputImage({
                tempId: null,
                url: '',
                objectUrl: newFile ? URL.createObjectURL(newFile) : '',
                file: newFile,
            });

            refresh();
        },
        [refresh, setInputImage, objectUrl]
    );

    useEffect(() => {
        if (!objectUrl) {
            return;
        }

        getImageFromUrl(objectUrl)
            .then((image: HTMLImageElement) => {
                const {naturalWidth, naturalHeight} = image;
                const isValid = naturalWidth >= minWidth && naturalHeight >= minHeight;

                if (!isValid) {
                    getUploadImageError(true);
                }
            })
            .catch(() => getUploadImageError(true));
    }, [objectUrl, getUploadImageError, relativeRealSize.width, relativeRealSize.height, minWidth, minHeight]);

    const handleModalCancel = useCallback(() => {
        resetImage();
        setIsModalOpen(false);
    }, [resetImage]);

    useEffect(() => {
        const {file} = inputImage;

        if (file) {
            setInnerInputImage(file);
            changeSizeParameters(objectUrl || URL.createObjectURL(file));
        }
    }, [changeSizeParameters, inputImage, objectUrl]);

    return (
        <>
            <InputImageContent
                className={className}
                drawImageUrl={drawImageUrl}
                noImageText={noImageText}
                resetImage={resetImage}
                setIsModalOpen={setIsModalOpen}
            />

            <Modal
                cancelText={<Locale stringKey="POPUP__BUTTON__CANCEL" />}
                centered
                destroyOnClose
                maskClosable={false}
                okText={<Locale stringKey="BUTTON__UPLOAD" />}
                onCancel={handleModalCancel}
                onOk={applyChanges}
                open={isModalOpen}
                title={<Locale stringKey="COMPANY_FORM__LOGO__UPLOAD_AND_CROP" />}
                width={1000}
            >
                <div
                    className={classNames(
                        styles.drop_photo_place,
                        inputActive ? styles.drop_photo_place_active : '',
                        drawImageUrl ? styles.drop_photo_place_editing : ''
                    )}
                    ref={targetRef}
                >
                    {drawImageUrl ? (
                        <ReactCrop
                            aspect={minWidth / minHeight}
                            crop={crop}
                            minHeight={minLocalSize.height}
                            minWidth={minLocalSize.width}
                            onChange={handleCropChange}
                            onComplete={handleSetCrop}
                        >
                            <img alt="" src={drawImageUrl} />
                        </ReactCrop>
                    ) : (
                        <>
                            <input
                                accept="image/jpeg, image/jpg, image/png, image/gif"
                                className={styles.drop_photo_place__input}
                                key={refreshId}
                                onChange={handleOnChange}
                                onDragEnter={() => setInputActive(true)}
                                onDragLeave={() => setInputActive(false)}
                                type="file"
                            />

                            <div className={styles.drop_photo_place__left}>
                                <h4 className={styles.drop_photo_place__header}>{modalHeader}</h4>

                                <Text className={styles.drop_photo_place__allowed_formats}>
                                    <Locale
                                        stringKey="COMPANY_FORM__LOGO__RESTRICTIONS"
                                        valueMap={{minWidth, minHeight}}
                                    />
                                </Text>
                            </div>

                            <div className={styles.drop_photo_place__right}>
                                <div className={styles.drop_photo_place__input_focus}>
                                    <FontAwesomeIcon icon={faFileArrowUp} />
                                </div>

                                <UploadButton className={styles.drop_photo_place__upload_button} />

                                <Text className={styles.drop_photo_place__text}>
                                    <Locale stringKey="COMPANY_FORM__LOGO__DRAG_FILE_HERE" />
                                </Text>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        </>
    );
}
